import { useState, useEffect, useRef } from 'react';
import Modal from './Modal';
import IconPicker from './IconPicker';

/**
 * Node / Folder Settings — unified centered modal popup.
 *
 * Triggered by the gear icon on any tree row (file or folder), or by
 * swipe-right on a file node (mobile).  Contains rename (files only),
 * icon picker, quick-access toggle, hide/show toggle, and delete.
 *
 * Props:
 *  - show               : boolean
 *  - type               : 'file' | 'folder'
 *  - nodePath           : string — full repo-relative path (read-only display)
 *  - nodeName           : string — original name (e.g. "mynote.md" or "Projects")
 *  - currentIcon        : string — currently assigned icon (empty = default)
 *  - isQuickAccess      : boolean
 *  - isHidden           : boolean
 *  - onSetIcon          : (icon: string) => void
 *  - onClearIcon        : () => void
 *  - onToggleQuickAccess : () => void
 *  - onToggleHidden     : () => void
 *  - onRename           : (newName: string) => void | Promise<string|null>
 *  - onDelete           : () => void
 *  - onClose            : () => void
 */

/** Inline toggle switch — visual + hidden checkbox. */
function Toggle({ checked, onChange, label }) {
  return (
    <label style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-sm)',
      cursor: 'pointer',
      fontSize: 'var(--text-sm)',
      color: 'var(--ink)',
    }}>
      <div style={{
        position: 'relative',
        width: 40,
        height: 22,
        borderRadius: 11,
        background: checked ? 'var(--accent)' : 'var(--ink-muted)',
        transition: 'background 0.2s',
        flexShrink: 0,
      }}>
        <div style={{
          position: 'absolute',
          top: 2,
          left: checked ? 20 : 2,
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: '#fff',
          transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
        readOnly
      />
      <span>{label}</span>
    </label>
  );
}

export default function NodeOptionsPanel({
  show,
  type = 'file',
  nodePath,
  nodeName,
  currentIcon,
  isQuickAccess,
  isHidden,
  onSetIcon,
  onClearIcon,
  onToggleQuickAccess,
  onToggleHidden,
  onRename,
  onDelete,
  onClose,
}) {
  const isFolder = type === 'folder';
  const [editName, setEditName] = useState('');
  const [fileExt, setFileExt] = useState('');
  const [renameError, setRenameError] = useState('');
  const [renameSubmitting, setRenameSubmitting] = useState(false);
  const inputRef = useRef(null);

  // Initialise input when panel opens — extract extension dynamically
  useEffect(() => {
    if (show && nodeName) {
      const lastDot = nodeName.lastIndexOf('.');
      const ext = lastDot !== -1 ? nodeName.substring(lastDot) : '';
      const base = ext ? nodeName.slice(0, -ext.length) : nodeName;
      setEditName(base);
      setFileExt(ext);
      setRenameError('');
      setRenameSubmitting(false);
      // Focus the input after the animation starts
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [show, nodeName]);

  // ESC to close
  useEffect(() => {
    if (!show) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [show, onClose]);

  const handleRename = async () => {
    const trimmed = editName.trim();
    if (!trimmed || renameSubmitting) return;
    if (!onRename) return;
    setRenameSubmitting(true);
    setRenameError('');
    try {
      const err = await onRename(trimmed);
      if (err) setRenameError(err);
    } catch (e) {
      setRenameError(e?.message || 'Rename failed');
    } finally {
      setRenameSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      title={isFolder ? 'Folder Settings' : 'Node Settings'}
      onClose={onClose}
      modalClass="modal-fixed"
      footer={
        <button
          className="v2-node-options-delete"
          onClick={() => { if (onDelete) onDelete(); }}
        >
          <span>🗑️</span>
          <span>{isFolder ? 'Delete folder' : 'Delete node'}</span>
        </button>
      }
    >
      {/* Path (read-only) */}
      <div style={{ marginBottom: 'var(--space-md)' }}>
        <div className="v2-section-label">Path</div>
        <div style={{
          fontSize: 'var(--text-sm)',
          color: 'var(--ink-muted)',
          fontFamily: 'var(--font-mono)',
          wordBreak: 'break-all',
        }}>
          {nodePath}
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: 'var(--rule)', margin: '0 0 var(--space-md) 0' }} />

      {/* Rename — files only (folder rename not supported) */}
      {!isFolder && (
        <>
          <div style={{ marginBottom: 'var(--space-md)' }}>
            <div className="v2-section-label">Rename</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 'var(--space-sm)' }}>
              <input
                ref={inputRef}
                style={{
                  flex: 1,
                  padding: 'var(--space-sm) var(--space-md)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '16px',
                  border: 'var(--border-thin)',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--paper)',
                  color: 'var(--ink)',
                  outline: 'none',
                }}
                value={editName}
                onChange={e => { setEditName(e.target.value); if (renameError) setRenameError(''); }}
                onKeyDown={e => { if (e.key === 'Enter') handleRename(); }}
                onFocus={e => e.target.select()}
              />
              {fileExt && (
                <span style={{
                  color: 'var(--ink-muted)',
                  flexShrink: 0,
                  fontSize: 'var(--text-sm)',
                  marginLeft: 'var(--space-xs)',
                }}>{fileExt}</span>
              )}
            </div>
            {renameError && (
              <div style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-sm)' }}>
                {renameError}
              </div>
            )}
            <button
              className="btn btn-primary"
              onClick={handleRename}
              disabled={!editName.trim() || renameSubmitting}
              style={{ width: '100%' }}
            >
              {renameSubmitting ? 'Renaming…' : 'Rename'}
            </button>
          </div>

          {/* Divider */}
          <div style={{ height: 1, background: 'var(--rule)', margin: '0 0 var(--space-md) 0' }} />
        </>
      )}

      {/* Quick Access toggle */}
      <div style={{ marginBottom: 'var(--space-md)' }}>
        <div className="v2-section-label">Quick Access</div>
        <Toggle
          checked={!!isQuickAccess}
          onChange={() => { if (onToggleQuickAccess) onToggleQuickAccess(); }}
          label={isQuickAccess ? 'In quick access' : 'Not in quick access'}
        />
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: 'var(--rule)', margin: '0 0 var(--space-md) 0' }} />

      {/* Visibility (hide / show in list) toggle */}
      <div style={{ marginBottom: 'var(--space-md)' }}>
        <div className="v2-section-label">Visibility</div>
        <Toggle
          checked={!!isHidden}
          onChange={() => { if (onToggleHidden) onToggleHidden(); }}
          label={isHidden ? 'Hidden from list' : 'Visible in list'}
        />
        <p style={{
          margin: 'var(--space-sm) 0 0',
          fontSize: 'var(--text-xs)',
          color: 'var(--ink-muted)',
        }}>
          Hidden items only appear when "Show hidden" is enabled in the Notes header.
        </p>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: 'var(--rule)', margin: '0 0 var(--space-md) 0' }} />

      {/* Icon picker */}
      <div>
        <div className="v2-section-label">Icon</div>
        <IconPicker
          selected={currentIcon || ''}
          onSelect={onSetIcon}
          onClear={onClearIcon}
        />
      </div>

    </Modal>
  );
}
